import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { backendService } from "../../../services/backend.service";
import { type AgentReportView, AgentSource, AgentStaffActionStatus, type DepartmentActionsMetadataResponse, type FlatNurseAction } from "../../../utils/models";

interface UseDepartmentActionsMetadataParams {
	startTime?: DateTime;
	endTime?: DateTime;
	requestId?: string;
	view?: AgentReportView;
	/** Extra gate on top of the department + CallOffAgent-flag check (e.g. only fetch raw in raw mode). */
	enabled?: boolean;
}

interface UseAgentHistoryParams extends UseDepartmentActionsMetadataParams {
	departmentId?: string;
}

export const useAgentHistory = ({ requestId, departmentId, startTime, endTime }: UseAgentHistoryParams) => {
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const { showSnackbar } = useSnackbar();
	const queryClient = useQueryClient();
	const deptId = departmentId || chosenDepartment?.department_id;
	const isCallOffEnabled = useFeatureFlag(FEATURE_FLAGS.CallOffAgent);
	const { data: departmentActionsMetadata, isLoading: isLoadingDepartmentMetadata } = useDepartmentActionsMetadata({ startTime, endTime, requestId });

	const {
		data: fetchedActions,
		isLoading: isLoadingAgentHistory,
		refetch: refetchAgentHistory,
	} = useQuery<FlatNurseAction[]>({
		queryKey: ["agent-history", requestId, deptId],
		queryFn: async () => {
			if (!requestId || !deptId) return [];
			const response = await backendService.getAgentNurseActions(requestId, deptId, AgentSource.CallOff);
			return response.data;
		},
		enabled: !!requestId && !!deptId && isCallOffEnabled,
	});

	const actions = fetchedActions ?? [];

	// Get unique agent IDs from all actions
	const agentIds = useMemo(() => {
		const uniqueIds = new Set<string>();
		actions.forEach((action) => {
			if (action.agent_action_id) {
				uniqueIds.add(action.agent_action_id);
			}
		});
		return Array.from(uniqueIds);
	}, [actions]);

	// Get actions for this specific request from department metadata
	const requestData = useMemo(() => {
		if (!requestId || !departmentActionsMetadata?.requests) return null;
		return departmentActionsMetadata.requests.find((req) => req.request_id === requestId) ?? null;
	}, [requestId, departmentActionsMetadata]);

	// requests.needed_nurses is the count still open (decremented on approval), not the original total.
	const approvedNurseCount = useMemo(() => new Set(actions.filter((action) => action.is_approved).map((action) => action.nurse_id)).size, [actions]);
	const totalNeededNurses = requestData ? approvedNurseCount + requestData.needed_nurses : undefined;

	// Pause by Request
	const pauseByRequestMutation = useMutation({
		mutationFn: async () => {
			if (!requestId || !deptId) return;
			await backendService.pauseShiftPickupAgent(requestId, deptId, AgentSource.CallOff);
		},
		onSuccess: async () => {
			showSnackbar("Agent paused successfully", "success");
			refetchAgentHistory();
			await queryClient.invalidateQueries({
				queryKey: ["departmentActionsMetadata", chosenDepartment?.department_id],
			});
		},
		onError: (error) => {
			showErrorSnackbar("Failed to pause agent", { action: "pause agent" }, error);
		},
	});

	// Resume by Request
	const resumeByRequestMutation = useMutation({
		mutationFn: async () => {
			if (!requestId || !deptId) return;
			await backendService.resumeShiftPickupAgent(requestId, deptId, AgentSource.CallOff);
		},
		onSuccess: async () => {
			showSnackbar("Agent resumed successfully", "success");
			refetchAgentHistory();
			await queryClient.invalidateQueries({
				queryKey: ["departmentActionsMetadata", chosenDepartment?.department_id],
			});
		},
		onError: (error) => {
			showErrorSnackbar("Failed to resume agent", { action: "resume agent" }, error);
		},
	});

	const isAnyMutationPending = pauseByRequestMutation.isPending || resumeByRequestMutation.isPending;

	return {
		// Agent history data
		data: actions,
		isLoading: isLoadingAgentHistory,
		refetch: refetchAgentHistory,
		// Agent IDs and metadata
		agentIds,
		agentStatus: requestData?.status,
		isLoadingMetadata: isLoadingDepartmentMetadata,
		totalNeededNurses,
		// Mutations
		isAnyMutationPending,
		pauseByRequestMutation,
		resumeByRequestMutation,
	};
};

export const useDepartmentActionsMetadata = (params?: UseDepartmentActionsMetadataParams) => {
	const { chosenDepartment } = useChosenDepartment();
	const isCallOffEnabled = useFeatureFlag(FEATURE_FLAGS.CallOffAgent);

	const dateRange = useMemo(() => {
		const callOffBypassHours = chosenDepartment?.callOffBypassHoursBeforeShiftAdmin ?? 24;

		const now = DateTime.now();
		let startTime = params?.startTime ? params.startTime : now.minus({ days: 1 });
		let endTime = params?.endTime ? params.endTime : now.plus({ hours: callOffBypassHours });

		startTime = startTime.set({ minute: 0, second: 0, millisecond: 0 });
		endTime = endTime.set({ minute: 0, second: 0, millisecond: 0 });

		return {
			startTime: startTime.toISO() ?? undefined,
			endTime: endTime.toISO() ?? undefined,
		};
	}, [params?.startTime, params?.endTime, chosenDepartment?.callOffBypassHoursBeforeShiftAdmin]);

	return useQuery<DepartmentActionsMetadataResponse | null>({
		// `view` is part of the key so grouped & raw are cached separately. The pause/resume mutations
		// invalidate by the `["departmentActionsMetadata", department_id]` prefix, which (being a prefix
		// match) still invalidates both views regardless of the trailing key segments.
		queryKey: ["departmentActionsMetadata", chosenDepartment?.department_id, params?.requestId, dateRange.startTime, dateRange.endTime, params?.view],
		queryFn: async () => {
			if (!chosenDepartment?.department_id) return null;
			const response = await backendService.getDepartmentActionsMetadata(
				chosenDepartment.department_id,
				dateRange.startTime,
				dateRange.endTime,
				params?.requestId,
				params?.view,
			);
			return response.data;
		},
		enabled: !!chosenDepartment?.department_id && isCallOffEnabled && (params?.enabled ?? true),
		staleTime: 1000 * 60 * 3, // 3 minutes
	});
};

export const useRunNurseActionMutation = (requestId: string | null | undefined) => {
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const { showSnackbar } = useSnackbar();
	const queryClient = useQueryClient();
	const deptId = chosenDepartment?.department_id;

	const runNurseActionMutation = useMutation({
		mutationFn: async ({ nurseActionId, isFuture }: { nurseActionId: string; isFuture: boolean }) => {
			if (!deptId) {
				throw new Error("Department is not selected");
			}
			await backendService.runNurseAction(nurseActionId, deptId, AgentSource.CallOff);
			return { nurseActionId, isFuture };
		},
		onMutate: async ({ nurseActionId }) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({ queryKey: ["agent-history", requestId, deptId] });

			const previousActions = queryClient.getQueryData<FlatNurseAction[]>(["agent-history", requestId, deptId]);

			if (previousActions) {
				const updatedActions = previousActions.map((action) => (action.id === nurseActionId ? { ...action, status: AgentStaffActionStatus.Loading } : action));
				queryClient.setQueryData<FlatNurseAction[]>(["agent-history", requestId, deptId], updatedActions);
			}

			return { previousActions };
		},
		onSuccess: async (data, variables) => {
			// Update status to completed before invalidating
			const currentActions = queryClient.getQueryData<FlatNurseAction[]>(["agent-history", requestId, deptId]);
			if (currentActions) {
				const updatedActions = currentActions.map((action) =>
					action.id === variables.nurseActionId ? { ...action, status: AgentStaffActionStatus.Completed } : action,
				);
				queryClient.setQueryData<FlatNurseAction[]>(["agent-history", requestId, deptId], updatedActions);
			}
			showSnackbar(data.isFuture ? "Action will run now" : "Reminder sent", "success");
			await queryClient.invalidateQueries({ queryKey: ["agent-history", requestId, deptId] });
		},
		onError: (error, _variables, context) => {
			if (context?.previousActions) {
				queryClient.setQueryData<FlatNurseAction[]>(["agent-history", requestId, deptId], context.previousActions);
			}
			showErrorSnackbar("Failed to trigger action", { action: "run nurse action" }, error);
		},
	});

	return runNurseActionMutation;
};

/** The call-off agent's "requests with a robo call out" query, by its key's first part — for invalidating it after a call starts. */
export const DEPARTMENT_REQUEST_AGENT_IDS_QUERY_KEY = "department-request-agent-ids";

export const useDepartmentReuqestAgentIds = (params?: UseDepartmentActionsMetadataParams) => {
	const { chosenDepartment } = useChosenDepartment();
	const isCallOffEnabled = useFeatureFlag(FEATURE_FLAGS.CallOffAgent);

	const dateRange = useMemo(() => {
		const callOffBypassHours = chosenDepartment?.callOffBypassHoursBeforeShiftAdmin ?? 24;

		const now = DateTime.now();
		let startTime = params?.startTime ? params.startTime : now.minus({ days: 1 });
		let endTime = params?.endTime ? params.endTime : now.plus({ hours: callOffBypassHours });

		startTime = startTime.set({ minute: 0, second: 0, millisecond: 0 });
		endTime = endTime.set({ minute: 0, second: 0, millisecond: 0 });

		return {
			startTime: startTime,
			endTime: endTime,
		};
	}, [params?.startTime, params?.endTime, chosenDepartment?.callOffBypassHoursBeforeShiftAdmin]);

	const requestsAgentQuery = useQuery<string[]>({
		queryKey: [DEPARTMENT_REQUEST_AGENT_IDS_QUERY_KEY, chosenDepartment.department_id, dateRange.startTime, dateRange.endTime],
		queryFn: async () => {
			if (!chosenDepartment.department_id) return [];
			const response = await backendService.getDepartmentAgentRequests(chosenDepartment.department_id, dateRange.startTime, dateRange.endTime);
			return response.data;
		},
		enabled: isCallOffEnabled,
		staleTime: 1000 * 60 * 3,
	});

	return requestsAgentQuery;
};
